import DestructorTree from "../DestructorTree";
import { api_configuration } from "../../common";
import { registerText } from "./registerClientState";
import api_cs = api_configuration.types.client_state.alan_api;
import { descendant_object_path } from "../state/expressions";
import { addChild } from "../DestructorTree";
import * as Status from "../Status";
import { linkIdPath } from "./ClientIdPath";
import { onScalarValue } from "../../../../../stream";

//TODO handle collection key clashes
export function applyCollectionJoin(join: api_cs.Cyes__joined, destructor: DestructorTree): void {
	let current_id = 0;
	const collection_key_definition = join.parent.inferences.collection_type().properties.key_property.ref;
	const joined_collection_key_definition = collection_key_definition.properties.has_reference
		.cast("yes")
		.properties.expression.output.collection().properties.key_property.ref;
	const key_tail = collection_key_definition.properties.has_reference.cast("yes").properties.expression.properties.tail;

	const joined_entry_map = new Map<string, api_cs.Centries__yes__joined>();

	const remove_entry = (key: string) => {
		const joined_entry = joined_entry_map.get(key);
		if (joined_entry !== undefined) {
			join.properties.entries.delete(key);
			joined_entry_map.delete(joined_entry.key_value);
		}
	};

	const remove_collection_entry = (key: string) => {
		const joined_entry = joined_entry_map.get(key);
		if (joined_entry !== undefined) {
			switch (joined_entry.properties.referenced_entry.state.name) {
				case "entry": {
					joined_entry.properties.collection_entry.set("none", {});
					break;
				}
				case "none": {
					remove_entry(key);
					break;
				}
				default:
					unexpectedState(joined_entry.properties.referenced_entry.state);
			}
		}
	};

	destructor.onDestroy(() => {
		joined_entry_map.clear();
		for (const entry of join.properties.entries.keys()) {
			join.properties.entries.delete(entry);
		}
	});

	const remove_referenced_entry = (key: string) => {
		const joined_entry = joined_entry_map.get(key);
		if (joined_entry !== undefined) {
			switch (joined_entry.properties.collection_entry.state.name) {
				case "entry": {
					joined_entry.properties.referenced_entry.set("none", {});
					break;
				}
				case "none": {
					remove_entry(key);
					break;
				}
				default:
					unexpectedState(joined_entry.properties.collection_entry.state);
			}
		}
	};

	destructor.subscribeToCollection(
		join.parent.properties.entries,
		{
			on_add: (entry, entry_destructor) => {
				const text = registerText(collection_key_definition, entry.properties.node, entry_destructor);
				let previous_key: string | undefined = undefined;
				const set_collection_entry = (key: string) => {
					if (previous_key !== undefined) {
						const old_joined_entry = joined_entry_map.get(previous_key);
						if (old_joined_entry !== undefined) {
							switch (old_joined_entry.properties.referenced_entry.state.name) {
								case "entry": {
									old_joined_entry.properties.collection_entry.set("none", {});
									break;
								}
								case "none": {
									remove_entry(previous_key);
									break;
								}
								default:
									unexpectedState(old_joined_entry.properties.referenced_entry.state);
							}
						}
					}
					previous_key = key;
					let joined_entry = joined_entry_map.get(key);
					if (joined_entry === undefined) {
						const entry_id = (current_id++).toString();
						joined_entry = join.properties.entries.create(entry_id, {
							"collection entry": [
								"entry",
								{
									entry: entry.key_value,
								},
							],
							"referenced entry": "none",
						});
						joined_entry_map.set(key, joined_entry);
					} else {
						joined_entry.properties.collection_entry.set("entry", {
							entry: entry.key_value,
						});
					}
				};
				entry_destructor.subscribeToStategroup(text.properties.is_set, (text_destructor) => {
					switch (text.properties.is_set.state.name) {
						case "no": {
							break;
						}
						case "yes": {
							const is_set = text.properties.is_set.state.node;
							text_destructor.subscribeToText(is_set, "subscribe_text", (text_value) => {
								if (text_value !== previous_key) {
									set_collection_entry(text_value);
								}
							});
							break;
						}
						default:
							unexpectedState(text.properties.is_set.state);
					}
				});
				entry_destructor.onDestroy(() => {
					if (previous_key !== undefined) {
						remove_collection_entry(previous_key);
					}
				});
			},
		},
		true
	);

	destructor.subscribeToStategroup(join.properties.joined_collection, (joined_collection_destructor) => {
		switch (join.properties.joined_collection.state.name) {
			case "resolved": {
				const resolved = join.properties.joined_collection.state.node;
				linkIdPath(resolved.properties.referenced_node);

				joined_collection_destructor.subscribeToCollection(
					resolved.properties.collection.ref.properties.entries,
					{
						on_add: (entry, entry_destructor) => {
							const text = registerText(joined_collection_key_definition, entry.properties.node, entry_destructor);
							let previous_key: string | undefined = undefined;
							let valid_candidate = false;
							const set_collection_entry = () => {
								let key: string | undefined;
								switch (text.properties.is_set.state.name) {
									case "no":
										break;
									case "yes": {
										const is_set = text.properties.is_set.state.node;
										key = is_set.properties.text;
										break;
									}
									default:
										unexpectedState(text.properties.is_set.state);
								}
								if (key === undefined || key === previous_key || !valid_candidate) {
									return;
								}
								if (previous_key !== undefined) {
									const old_joined_entry = joined_entry_map.get(previous_key);
									if (old_joined_entry !== undefined) {
										switch (old_joined_entry.properties.referenced_entry.state.name) {
											case "entry": {
												old_joined_entry.properties.referenced_entry.set("none", {});
												break;
											}
											case "none": {
												remove_entry(previous_key);
												break;
											}
											default:
												unexpectedState(old_joined_entry.properties.referenced_entry.state);
										}
									}
								}
								previous_key = key;
								let joined_entry = joined_entry_map.get(key);
								if (joined_entry === undefined) {
									const entry_id = (current_id++).toString();
									joined_entry = join.properties.entries.create(entry_id, {
										"referenced entry": [
											"entry",
											{
												entry: entry.key_value,
												"can create entry": "no",
											},
										],
										"collection entry": "none",
									});
									joined_entry_map.set(key, joined_entry);
								} else {
									joined_entry.properties.referenced_entry.set("entry", {
										entry: entry.key_value,
										"can create entry": "no",
									});
								}

								entry_destructor.subscribeToStategroup(join.parent.properties.can_create_entry, () => {
									switch (joined_entry!.properties.referenced_entry.state.name) {
										case "entry": {
											const entry = joined_entry!.properties.referenced_entry.state.node;
											switch (join.parent.properties.can_create_entry.state.name) {
												case "no": {
													switch (entry.properties.can_create_entry.state.name) {
														case "no":
															break;
														case "yes": {
															entry.properties.can_create_entry.set("no", {});
															break;
														}
														default:
															unexpectedState(entry.properties.can_create_entry.state);
													}
													break;
												}
												case "yes": {
													switch (entry.properties.can_create_entry.state.name) {
														case "no": {
															entry.properties.can_create_entry.set("yes", {});
															break;
														}
														case "yes":
															break;
														default:
															unexpectedState(entry.properties.can_create_entry.state);
													}
													break;
												}
												default:
													unexpectedState(join.parent.properties.can_create_entry.state);
											}
											break;
										}
										case "none":
											break;
										default:
											unexpectedState(joined_entry!.properties.referenced_entry.state);
									}
								});
							};
							const unset_collection_entry = () => {
								if (previous_key === undefined) {
									return;
								}
								remove_referenced_entry(previous_key);
								previous_key = undefined;
							};

							const tail_expression = descendant_object_path(key_tail)(entry.properties.node);
							onScalarValue(
								tail_expression,
								(tail_context) => {
									//TODO how to distinguish insufficient rights?
									//TODO edg: add insufficient rights option to Async enum?
									// switch (tail_context.type) {
									// 	case "insufficient rights": {
									// 		valid_candidate = true;
									// 		set_collection_entry();
									// 		break;
									// 	}
									// 	case "unexpected": {
									// 		valid_candidate = false;
									// 		unset_collection_entry();
									// 		break;
									// 	}
									// 	case "dependency not set": {
									// 		valid_candidate = false;
									// 		unset_collection_entry();
									// 		break;
									// 	}
									// 	default:
									// 		unexpectedState(tail_context.type);
									// }
									if (Status.isResolved(tail_context)) {
										valid_candidate = true;
										set_collection_entry();
									} else {
										valid_candidate = false;
										unset_collection_entry();
									}
								},
								(scalar, subscription) => entry_destructor.onDestroy(() => scalar.unsubscribe(subscription))
							);

							entry_destructor.subscribeToStategroup(text.properties.is_set, (text_destructor) => {
								switch (text.properties.is_set.state.name) {
									case "no":
										break;
									case "yes": {
										const is_set = text.properties.is_set.state.node;
										text_destructor.subscribeToText(is_set, "subscribe_text", () => {
											set_collection_entry();
										});
										break;
									}
									default:
										unexpectedState(text.properties.is_set.state);
								}
							});
							entry_destructor.onDestroy(() => {
								if (previous_key !== undefined) {
									remove_referenced_entry(previous_key);
								}
							});
						},
					},
					true
				);
				break;
			}
			case "unresolved": {
				for (const [key] of join.properties.entries) {
					remove_referenced_entry(key);
				}
				break;
			}
			default:
				unexpectedState(join.properties.joined_collection.state);
		}
	});
	setCollectionJoinSizeSubscriptions(join);
}

export function setCollectionJoinSizeSubscriptions(join: api_cs.Cyes__joined): void {
	const destructor = addChild(join);

	destructor.subscribeToCollection(
		join.properties.entries,
		{
			on_change: () => {
				const collection_size = join.properties.entries.size;
				join.set_amount_of_entries(collection_size);
				switch (collection_size) {
					case 0:
						switch (join.properties.is_empty.state.name) {
							case "yes":
								break;
							case "no":
								join.properties.is_empty.set("yes", {});
								break;
							default:
								unexpectedState(join.properties.is_empty.state);
						}
						break;
					case 1:
						switch (join.properties.is_empty.state.name) {
							case "yes":
								join.properties.is_empty.set("no", {
									size: ["single", {}],
								});
								break;
							case "no": {
								const $ = join.properties.is_empty.state.node;
								switch ($.properties.size.state.name) {
									case "single":
										break;
									case "multiple":
										$.properties.size.set("single", {});
										break;
									default:
										unexpectedState($.properties.size.state);
								}
								break;
							}
							default:
								unexpectedState(join.properties.is_empty.state);
						}
						break;
					default:
						switch (join.properties.is_empty.state.name) {
							case "yes":
								join.properties.is_empty.set("no", {
									size: ["multiple", {}],
								});
								break;
							case "no": {
								const $ = join.properties.is_empty.state.node;
								switch ($.properties.size.state.name) {
									case "single":
										$.properties.size.set("multiple", {});
										break;
									case "multiple":
										break;
									default:
										unexpectedState($.properties.size.state);
								}
								break;
							}
							default:
								unexpectedState(join.properties.is_empty.state);
						}
						break;
				}
			},
		},
		/*init=*/ true
	);
}
